/**
 * CBAParamsForm - Redesigned clean CBA parameter form.
 *
 * Props:
 *   - params: Object with all CBA parameters
 *   - onChange: Callback(updatedParams) when any field changes
 *   - community: Community data for auto-populated fields
 *
 * Organized into clean card sections:
 *   A) Scenario Definition (What to Analyze)
 *   B) Investment Costs (PV & Battery)
 *   C) Energy Tariffs (with sliders)
 *   D) Financial & Environmental
 */

import React, { useState, useEffect } from 'react'
import { Form, Row, Col, Card, Badge, Alert } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'

// EUR -> CZK exchange rate for input conversion.
const EUR_TO_CZK = 25

function CBAParamsForm({ params, onChange, community, computedAnnualLoad }) {
  const { t } = useTranslation()
  const currency = community?.currency || 'CZK'
  const currLabel = currency === 'CZK' ? 'CZK' : 'EUR'
  const isCZK = currency === 'CZK'

  // Defaults are in the community's local currency.
  // CZK values are EUR × 25 so users see familiar local prices.
  const defaults = {
    // A) Scenario Definition
    cbaScenarios: ['pv_only'],  // Which investment scenarios to analyze
    newPvCapacityKwp: 50,
    newBatteryCapacityKwh: 100,
    analysisPeriodYears: 20,

    // B) Investment Costs - PV (in local currency)
    pvCapexPerKwp: isCZK ? 25000 : 1000,
    pvAnnualMaintenancePerKwp: isCZK ? 375 : 15,
    pvAnnualDegradationPct: 0.5,

    // B) Investment Costs - Battery (in local currency)
    battCapexPerKwh: isCZK ? 10000 : 400,
    battAnnualMaintenancePerKwh: isCZK ? 250 : 10,
    battLifetimeYears: 15,
    battReplacementCostPct: 60,

    // C) Energy Tariffs (in local currency per kWh)
    electricityPurchasePricePerKwh: isCZK ? 5.50 : 0.15,
    purchaseTariffAnnualIncreasePct: 3,
    feedInTariffPerKwh: isCZK ? 2.00 : 0.06,
    feedInTariffAnnualIncreasePct: 3,

    // D) Financial & Environmental
    discountRatePct: 5,
    gridCO2intensityKgPerKwh: 0.4,
    environmentalCostEur: 0,

    // Auto-populated (read-only from community)
    existingPvKwp: community?.community_assets?.find(a => ['solar_pv_farm', 'ground_mounted_pv', 'pv'].includes(a.asset_type))?.specs?.capacity_kwp || 0,
    existingBatteryKwh: community?.community_assets?.find(a => a.asset_type === 'community_battery')?.specs?.capacity_kwh || 0,
    annualLoadKwh: computedAnnualLoad || 65000,

    // Track currency so consumers know which currency the values are in
    currency,
  }

  const [localParams, setLocalParams] = useState(params || { ...defaults })

  // Sync defaults to parent on mount so review panel has data
  useEffect(() => {
    if (!params && onChange) onChange({ ...defaults })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Update annual load when computed value arrives from datasets
  useEffect(() => {
    if (computedAnnualLoad && computedAnnualLoad > 0) {
      setLocalParams(prev => {
        // Only auto-update if the current value is the old placeholder or default
        if (prev.annualLoadKwh === 420000 || prev.annualLoadKwh === 65000) {
          const updated = { ...prev, annualLoadKwh: computedAnnualLoad }
          if (onChange) onChange(updated)
          return updated
        }
        return prev
      })
    }
  }, [computedAnnualLoad]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleFieldChange = (field, value) => {
    const updated = { ...localParams, [field]: value }
    setLocalParams(updated)
    if (onChange) onChange(updated)
  }

  const handleScenarioToggle = (scenario) => {
    const current = localParams.cbaScenarios || ['pv_only']
    let updated
    if (current.includes(scenario)) {
      updated = current.filter(s => s !== scenario)
      if (updated.length === 0) return // Must have at least one
    } else {
      updated = [...current, scenario]
    }
    handleFieldChange('cbaScenarios', updated)
  }

  const selectedScenarios = localParams.cbaScenarios || ['pv_only']
  const hasBessScenario = selectedScenarios.includes('bess_only') || selectedScenarios.includes('pv_bess')

  return (
    <div className="cba-params-form">
      {/* Auto-populated Community Data Banner */}
      <Alert variant="info" style={{ fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-4)' }}>
        <strong>{t('cba_params.banner_label')}</strong> {t('cba_params.banner_buildings', { count: community?.buildings?.length || 0 })} •
        {' '}{t('cba_params.banner_pv', { kwp: localParams.existingPvKwp })} •
        {' '}{t('cba_params.banner_battery', { kwh: localParams.existingBatteryKwh })} •
        {' '}{t('cba_params.banner_load', { mwh: (localParams.annualLoadKwh / 1000).toFixed(0) })}{computedAnnualLoad ? ' ' + t('cba_params.banner_source_datasets') : ' ' + t('cba_params.banner_source_default')}
      </Alert>

      {/* A) Scenario Definition */}
      <Card className="mb-4" style={{ border: '1px solid var(--color-primary-light)', borderRadius: 'var(--radius-lg)' }}>
        <div style={{ backgroundColor: 'var(--color-primary-pale)', padding: 'var(--space-4)', borderBottom: '1px solid var(--color-primary-light)' }}>
          <h6 style={{ fontWeight: 600, color: 'var(--color-primary-dark)', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            {t('cba_params.scenario_title')}
          </h6>
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>
            {t('cba_params.scenario_subtitle')}
          </div>
        </div>
        <Card.Body style={{ padding: 'var(--space-5)' }}>
          {/* Scenario Selection Checkboxes */}
          <div className="mb-4">
            <Form.Label style={{ fontWeight: 600, marginBottom: 'var(--space-2)' }}>{t('cba_params.scenarios_label')}</Form.Label>
            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)', marginBottom: 'var(--space-3)' }}>
              {t('cba_params.scenarios_help')}
            </div>
            <div className="d-flex gap-4 flex-wrap">
              {[
                { key: 'pv_only', label: t('cba_params.scen_pv_only'), desc: t('cba_params.scen_pv_only_desc') },
                { key: 'bess_only', label: t('cba_params.scen_bess_only'), desc: t('cba_params.scen_bess_only_desc') },
                { key: 'pv_bess', label: t('cba_params.scen_pv_bess'), desc: t('cba_params.scen_pv_bess_desc') },
              ].map(({ key, label, desc }) => (
                <div
                  key={key}
                  onClick={() => handleScenarioToggle(key)}
                  style={{
                    border: `2px solid ${selectedScenarios.includes(key) ? 'var(--color-primary)' : 'var(--color-neutral-300)'}`,
                    borderRadius: 'var(--radius-lg)',
                    padding: 'var(--space-3) var(--space-4)',
                    cursor: 'pointer',
                    backgroundColor: selectedScenarios.includes(key) ? 'var(--color-primary-pale)' : 'transparent',
                    minWidth: '150px',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <Form.Check
                    type="checkbox"
                    checked={selectedScenarios.includes(key)}
                    onChange={() => handleScenarioToggle(key)}
                    label={<span style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>{label}</span>}
                  />
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)', marginTop: '2px', paddingLeft: '1.5rem' }}>
                    {desc}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Row className="g-4">
            <Col md={4}>
              <Form.Label style={{ fontWeight: 500 }}>{t('cba_params.new_pv_label')}</Form.Label>
              <div className="d-flex align-items-center gap-2 mb-2">
                <Form.Range
                  min={0}
                  max={200}
                  step={5}
                  value={localParams.newPvCapacityKwp}
                  onChange={(e) => handleFieldChange('newPvCapacityKwp', parseInt(e.target.value))}
                  style={{ flex: 1 }}
                  disabled={!selectedScenarios.includes('pv_only') && !selectedScenarios.includes('pv_bess')}
                />
                <Badge bg="primary" style={{ minWidth: '60px', textAlign: 'center' }}>
                  {localParams.newPvCapacityKwp} kWp
                </Badge>
              </div>
              <div className="d-flex justify-content-between" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)' }}>
                <span>0 kWp</span>
                <span>200 kWp</span>
              </div>
            </Col>
            <Col md={4}>
              <Form.Label style={{ fontWeight: 500 }}>{t('cba_params.new_battery_label')}</Form.Label>
              <div className="d-flex align-items-center gap-2 mb-2">
                <Form.Range
                  min={0}
                  max={500}
                  step={10}
                  value={localParams.newBatteryCapacityKwh}
                  onChange={(e) => handleFieldChange('newBatteryCapacityKwh', parseInt(e.target.value))}
                  style={{ flex: 1 }}
                  disabled={!hasBessScenario}
                />
                <Badge bg={hasBessScenario ? 'primary' : 'secondary'} style={{ minWidth: '60px', textAlign: 'center' }}>
                  {localParams.newBatteryCapacityKwh} kWh
                </Badge>
              </div>
              <div className="d-flex justify-content-between" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)' }}>
                <span>0 kWh</span>
                <span>500 kWh</span>
              </div>
            </Col>
            <Col md={4}>
              <Form.Label style={{ fontWeight: 500 }}>{t('cba_params.analysis_period_label')}</Form.Label>
              <Form.Select
                value={localParams.analysisPeriodYears}
                onChange={(e) => handleFieldChange('analysisPeriodYears', parseInt(e.target.value))}
              >
                <option value={10}>{t('cba_params.period_10y')}</option>
                <option value={15}>{t('cba_params.period_15y')}</option>
                <option value={20}>{t('cba_params.period_20y')}</option>
                <option value={25}>{t('cba_params.period_25y')}</option>
              </Form.Select>
              <Form.Text className="text-muted">{t('cba_params.period_help')}</Form.Text>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* B) Investment Costs */}
      <Card className="mb-4" style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-lg)' }}>
        <div style={{ backgroundColor: 'var(--color-neutral-50)', padding: 'var(--space-4)', borderBottom: '1px solid var(--color-neutral-200)' }}>
          <h6 style={{ fontWeight: 600, color: 'var(--color-neutral-900)', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <span style={{ fontSize: '1.25rem' }}>💰</span> {t('cba_params.invest_title')}
          </h6>
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>
            {t('cba_params.invest_subtitle')}
          </div>
        </div>
        <Card.Body style={{ padding: 'var(--space-5)' }}>
          {/* PV System */}
          <h6 style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--space-3)', color: 'var(--color-neutral-700)' }}>
            {t('cba_params.pv_section')}
          </h6>
          <Row className="g-3 mb-4">
            <Col md={4}>
              <Form.Label>{t('cba_params.pv_installed_cost')}</Form.Label>
              <Form.Control
                type="number"
                min={isCZK ? 12500 : 500}
                max={isCZK ? 50000 : 2000}
                step={isCZK ? 250 : 10}
                value={localParams.pvCapexPerKwp}
                onChange={(e) => handleFieldChange('pvCapexPerKwp', parseFloat(e.target.value))}
              />
              <Form.Text className="text-muted">{t('cba_params.pv_per_kwp', { currency: currLabel })}</Form.Text>
            </Col>
            <Col md={4}>
              <Form.Label>{t('cba_params.pv_annual_maintenance')}</Form.Label>
              <Form.Control
                type="number"
                min={isCZK ? 125 : 5}
                max={isCZK ? 1250 : 50}
                step={isCZK ? 25 : 1}
                value={localParams.pvAnnualMaintenancePerKwp}
                onChange={(e) => handleFieldChange('pvAnnualMaintenancePerKwp', parseFloat(e.target.value))}
              />
              <Form.Text className="text-muted">{t('cba_params.pv_per_kwp_per_yr', { currency: currLabel })}</Form.Text>
            </Col>
            <Col md={4}>
              <Form.Label>{t('cba_params.pv_annual_degradation')}</Form.Label>
              <Form.Control
                type="number"
                min={0}
                max={2}
                step={0.1}
                value={localParams.pvAnnualDegradationPct}
                onChange={(e) => handleFieldChange('pvAnnualDegradationPct', parseFloat(e.target.value))}
              />
              <Form.Text className="text-muted">{t('cba_params.pv_per_year')}</Form.Text>
            </Col>
          </Row>

          {/* Battery System */}
          <h6 style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--space-3)', color: hasBessScenario ? 'var(--color-neutral-700)' : 'var(--color-neutral-400)' }}>
            {t('cba_params.battery_section')} {!hasBessScenario && <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 400 }}>{t('cba_params.battery_disabled_hint')}</span>}
          </h6>
          <Row className="g-3" style={{ opacity: hasBessScenario ? 1 : 0.5 }}>
            <Col md={3}>
              <Form.Label>{t('cba_params.batt_installed_cost')}</Form.Label>
              <Form.Control
                type="number"
                min={isCZK ? 5000 : 200}
                max={isCZK ? 20000 : 800}
                step={isCZK ? 250 : 10}
                value={localParams.battCapexPerKwh}
                onChange={(e) => handleFieldChange('battCapexPerKwh', parseFloat(e.target.value))}
                disabled={!hasBessScenario}
              />
              <Form.Text className="text-muted">{t('cba_params.batt_per_kwh', { currency: currLabel })}</Form.Text>
            </Col>
            <Col md={3}>
              <Form.Label>{t('cba_params.batt_annual_maintenance')}</Form.Label>
              <Form.Control
                type="number"
                min={isCZK ? 125 : 5}
                max={isCZK ? 750 : 30}
                step={isCZK ? 25 : 1}
                value={localParams.battAnnualMaintenancePerKwh}
                onChange={(e) => handleFieldChange('battAnnualMaintenancePerKwh', parseFloat(e.target.value))}
                disabled={!hasBessScenario}
              />
              <Form.Text className="text-muted">{t('cba_params.batt_per_kwh_per_yr', { currency: currLabel })}</Form.Text>
            </Col>
            <Col md={3}>
              <Form.Label>{t('cba_params.batt_lifetime')}</Form.Label>
              <Form.Control
                type="number"
                min={5}
                max={25}
                step={1}
                value={localParams.battLifetimeYears}
                onChange={(e) => handleFieldChange('battLifetimeYears', parseInt(e.target.value))}
                disabled={!hasBessScenario}
              />
              <Form.Text className="text-muted">{t('cba_params.years')}</Form.Text>
            </Col>
            <Col md={3}>
              <Form.Label>{t('cba_params.batt_replacement_cost')}</Form.Label>
              <Form.Control
                type="number"
                min={30}
                max={100}
                step={5}
                value={localParams.battReplacementCostPct}
                onChange={(e) => handleFieldChange('battReplacementCostPct', parseFloat(e.target.value))}
                disabled={!hasBessScenario}
              />
              <Form.Text className="text-muted">{t('cba_params.pct_original')}</Form.Text>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* C) Energy Tariffs */}
      <Card className="mb-4" style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-lg)' }}>
        <div style={{ backgroundColor: 'var(--color-neutral-50)', padding: 'var(--space-4)', borderBottom: '1px solid var(--color-neutral-200)' }}>
          <h6 style={{ fontWeight: 600, color: 'var(--color-neutral-900)', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <span style={{ fontSize: '1.25rem' }}>⚡</span> {t('cba_params.tariffs_title')}
          </h6>
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>
            {t('cba_params.tariffs_subtitle')}
          </div>
        </div>
        <Card.Body style={{ padding: 'var(--space-5)' }}>
          <Row className="g-4">
            <Col md={6}>
              <Form.Label style={{ fontWeight: 500 }}>{t('cba_params.purchase_price_label')}</Form.Label>
              <div className="d-flex align-items-center gap-2 mb-2">
                <Form.Range
                  min={isCZK ? 1.25 : 0.05}
                  max={isCZK ? 10.00 : 0.40}
                  step={isCZK ? 0.25 : 0.01}
                  value={localParams.electricityPurchasePricePerKwh}
                  onChange={(e) => handleFieldChange('electricityPurchasePricePerKwh', parseFloat(e.target.value))}
                  style={{ flex: 1 }}
                />
                <Badge bg="warning" text="dark" style={{ minWidth: '90px', textAlign: 'center' }}>
                  {localParams.electricityPurchasePricePerKwh.toFixed(isCZK ? 2 : 3)} {currLabel}/kWh
                </Badge>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)' }}>
                  <span>{isCZK ? '1.25' : '0.05'}</span> — <span>{isCZK ? '10.00' : '0.40'} {currLabel}/kWh</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Form.Label style={{ fontSize: 'var(--font-size-xs)', marginBottom: 0, whiteSpace: 'nowrap' }}>
                    {t('cba_params.annual_increase')}
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    max={10}
                    step={0.5}
                    value={localParams.purchaseTariffAnnualIncreasePct}
                    onChange={(e) => handleFieldChange('purchaseTariffAnnualIncreasePct', parseFloat(e.target.value))}
                    style={{ width: '70px', fontSize: 'var(--font-size-xs)' }}
                  />
                  <span style={{ fontSize: 'var(--font-size-xs)' }}>%</span>
                </div>
              </div>
            </Col>
            <Col md={6}>
              <Form.Label style={{ fontWeight: 500 }}>{t('cba_params.feedin_label')}</Form.Label>
              <div className="d-flex align-items-center gap-2 mb-2">
                <Form.Range
                  min={isCZK ? 0.50 : 0.02}
                  max={isCZK ? 3.75 : 0.15}
                  step={isCZK ? 0.25 : 0.01}
                  value={localParams.feedInTariffPerKwh}
                  onChange={(e) => handleFieldChange('feedInTariffPerKwh', parseFloat(e.target.value))}
                  style={{ flex: 1 }}
                />
                <Badge bg="success" style={{ minWidth: '90px', textAlign: 'center' }}>
                  {localParams.feedInTariffPerKwh.toFixed(isCZK ? 2 : 3)} {currLabel}/kWh
                </Badge>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)' }}>
                  <span>{isCZK ? '0.50' : '0.02'}</span> — <span>{isCZK ? '3.75' : '0.15'} {currLabel}/kWh</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Form.Label style={{ fontSize: 'var(--font-size-xs)', marginBottom: 0, whiteSpace: 'nowrap' }}>
                    {t('cba_params.annual_increase')}
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    max={10}
                    step={0.5}
                    value={localParams.feedInTariffAnnualIncreasePct}
                    onChange={(e) => handleFieldChange('feedInTariffAnnualIncreasePct', parseFloat(e.target.value))}
                    style={{ width: '70px', fontSize: 'var(--font-size-xs)' }}
                  />
                  <span style={{ fontSize: 'var(--font-size-xs)' }}>%</span>
                </div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* D) Financial & Environmental */}
      <Card className="mb-4" style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-lg)' }}>
        <div style={{ backgroundColor: 'var(--color-neutral-50)', padding: 'var(--space-4)', borderBottom: '1px solid var(--color-neutral-200)' }}>
          <h6 style={{ fontWeight: 600, color: 'var(--color-neutral-900)', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <span style={{ fontSize: '1.25rem' }}>📊</span> {t('cba_params.financial_title')}
          </h6>
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>
            {t('cba_params.financial_subtitle')}
          </div>
        </div>
        <Card.Body style={{ padding: 'var(--space-5)' }}>
          <Row className="g-4">
            <Col md={6}>
              <Form.Label style={{ fontWeight: 500 }}>{t('cba_params.discount_rate')}</Form.Label>
              <div className="d-flex align-items-center gap-2 mb-2">
                <Form.Range
                  min={1}
                  max={12}
                  step={0.5}
                  value={localParams.discountRatePct}
                  onChange={(e) => handleFieldChange('discountRatePct', parseFloat(e.target.value))}
                  style={{ flex: 1 }}
                />
                <Badge bg="secondary" style={{ minWidth: '60px', textAlign: 'center' }}>
                  {localParams.discountRatePct.toFixed(1)}%
                </Badge>
              </div>
              <div className="d-flex justify-content-between" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)' }}>
                <span>1%</span>
                <span>12%</span>
              </div>
            </Col>
            <Col md={6}>
              <Form.Label style={{ fontWeight: 500 }}>
                {t('cba_params.grid_co2')}
                <span
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-neutral-500)',
                    marginLeft: 'var(--space-2)',
                    fontWeight: 400,
                    cursor: 'help'
                  }}
                  title={t('cba_params.grid_co2_tooltip')}
                >
                  ⓘ
                </span>
              </Form.Label>
              <Form.Control
                type="number"
                min={0.1}
                max={1.0}
                step={0.01}
                value={localParams.gridCO2intensityKgPerKwh}
                onChange={(e) => handleFieldChange('gridCO2intensityKgPerKwh', parseFloat(e.target.value))}
              />
              <Form.Text className="text-muted">
                {t('cba_params.grid_co2_help')}
              </Form.Text>
            </Col>
          </Row>

          <Row className="g-4 mt-2">
            <Col md={6}>
              <Form.Label style={{ fontWeight: 500 }}>
                {t('cba_params.env_cost')}
                <span
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-neutral-500)',
                    marginLeft: 'var(--space-2)',
                    fontWeight: 400,
                    cursor: 'help'
                  }}
                  title={t('cba_params.env_cost_tooltip')}
                >
                  ⓘ
                </span>
              </Form.Label>
              <Form.Control
                type="number"
                min={0}
                max={isCZK ? 2500000 : 100000}
                step={isCZK ? 2500 : 100}
                value={localParams.environmentalCostEur}
                onChange={(e) => handleFieldChange('environmentalCostEur', parseFloat(e.target.value) || 0)}
              />
              <Form.Text className="text-muted">
                {t('cba_params.env_cost_help', { currency: currLabel })}
              </Form.Text>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  )
}

export default CBAParamsForm
